import jwtDecode from 'jwt-decode'
import { ROLE_TYPES } from 'rbac/roles'
import { isRBACEnabled } from 'tools/featureFlags'
import strings from 'strings'

interface JwtPayload {
  roleType?: string
}

const setupLoginCred = (data: any) => {
  if (!data) return
  localStorage.setItem('authToken', data?.AuthToken)
  localStorage.setItem('userName', data?.UserName)
  localStorage.setItem('userLogin', data?.UserLogin)
  localStorage.setItem('userType', data?.UserType)
  localStorage.setItem('userId', data?.UserId)
  localStorage.setItem('companyProfile', JSON.stringify(data?.Company_Profile))
  localStorage.setItem('companyId', data?.CompanyId)
  localStorage.setItem('companyName', data?.Company_Name)
  localStorage.setItem('companyTypeId', data?.CompanyTypeId)
  localStorage.setItem('userTypeId', data?.UserTypeId)
  localStorage.setItem('isInsurance', data?.Is_Insurance)
  localStorage.setItem(
    'tagCategories',
    data?.tagCategories
      ? JSON.stringify(data?.tagCategories)
      : JSON.stringify([])
  )
  if (data?.companyRadiusDefaults) {
    localStorage.setItem(
      'companyRadiusDefaults',
      JSON.stringify(data.companyRadiusDefaults)
    )
  } else {
    localStorage.removeItem('companyRadiusDefaults')
  }
  window.dispatchEvent(
    new CustomEvent('authStateChange', { detail: { userId: data?.UserId } })
  )
}

const getStoredCompanyRadiusDefaults = (): {
  radius1: number
  radius2: number
  radius3: number
  radius4: number
} | null => {
  try {
    const raw = localStorage.getItem('companyRadiusDefaults')
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const updateStoredCompanyRadiusDefaults = (values: {
  radius1: number
  radius2: number
  radius3: number
  radius4: number
}): void => {
  localStorage.setItem('companyRadiusDefaults', JSON.stringify(values))
}

const isCarrierLogin = () => {
  if (localStorage.getItem('companyTypeId') === '3') {
    return true
  }
  return false
}

const isAgentLogin = () => {
  if (localStorage.getItem('companyTypeId') === '10') {
    return true
  }
  return false
}

const isInsuranceLogin = () => {
  if (
    localStorage.getItem('isInsurance') === 'true' ||
    localStorage.getItem('companyTypeId') === '7'
  ) {
    return true
  }
  return false
}

let cachedToken: string | null = null
let cachedRoleType: string | null = null

const getRoleType = (): string | null => {
  const token = localStorage.getItem('authToken')
  if (!token) {
    cachedToken = null
    cachedRoleType = null
    return null
  }
  if (token === cachedToken) return cachedRoleType
  try {
    cachedRoleType = jwtDecode<JwtPayload>(token).roleType ?? null
    cachedToken = token
    return cachedRoleType
  } catch {
    cachedToken = null
    cachedRoleType = null
    return null
  }
}

const isViewOnly = (): boolean =>
  isRBACEnabled() && getRoleType() === ROLE_TYPES.VIEW_ONLY

const isAdmin = (): boolean =>
  isRBACEnabled() && getRoleType() === ROLE_TYPES.ADMIN

/**
 * Unlike isViewOnly(), this is NOT sprinkled through components to hide buttons. It is read in one
 * place — the insuranceMenu canViewMenu() filter — because that filter drives both the sidebar and
 * ProtectedRoute. The only other use is the post-login landing redirect.
 */
const isClaimsOnly = (): boolean =>
  isRBACEnabled() && getRoleType() === ROLE_TYPES.CLAIMS_ONLY

/**
 * True for the post-SSO landing route, /login/sso/:token/:userId.
 *
 * That URL always carries a *new* session. Whatever token is still in localStorage belongs to the
 * previous one and was just superseded server-side (single-session enforcement overwrote
 * user_login.Authtoken when the SSO callback minted the new token). Treating it as "logged in"
 * mounts the authenticated shell, whose first requests (feature-flags, report-configs) then answer
 * 401 "Seems you are logged in another machine" and can race the fresh token out of storage.
 */
const isSsoLandingPath = (path: unknown): boolean =>
  typeof path === 'string' && path.startsWith(`${strings.ROUTES.LOGIN_SSO}/`)

/**
 * Drops the previous session's token when the app is rendering the SSO landing route, so the shell
 * renders unauthenticated until setupLoginCred stores the new one. Returns true when it did so.
 * TRUCK-8262.
 */
const clearStaleSessionForSsoLanding = (path: unknown): boolean => {
  if (!isSsoLandingPath(path)) return false
  localStorage.removeItem('authToken')
  return true
}

export {
  setupLoginCred,
  isCarrierLogin,
  isAgentLogin,
  isInsuranceLogin,
  getStoredCompanyRadiusDefaults,
  updateStoredCompanyRadiusDefaults,
  getRoleType,
  isViewOnly,
  isAdmin,
  isClaimsOnly,
  isSsoLandingPath,
  clearStaleSessionForSsoLanding,
}
